import { useState, useContext } from 'react';
import {
  Box,
  Text,
  Flex,
  Divider,
  useToast,
  useDisclosure,
} from '@chakra-ui/react';
import { useStripe } from '@stripe/react-stripe-js';
import {
  monthlyEquivalentFromCustomOffer,
  customOfferBilledIntervalPhrase,
  formatGbpFromMinorUnits,
  annualOptionSavingPercent,
} from '../../../../utils/subscriptionUtils';
import { useBilling } from '../../../../store/client/BillingContext';
import { MeContext } from '../../../../context/MeContext';
import CardlessTrialConversionModal from '../CardlessTrialConversionModal';
import CustomToast from '../../../../common/CustomToast';
import DeclineCustomPlanModal from '../DeclineCustomPlanModal';
import GradientBorderCard from '../../../../common/GradientBorderCard';
import AcceptCustomPlanModal from '../AcceptCustomPlanModal';
import RequestChangesModal from '../RequestChangesModal';
import RequestCustomPlanModal from '../RequestCustomPlanModal';
import RequestedEntitlementsList from '../RequestedEntitlementsList';
import MonthlyAnnualToggle from '../MonthlyAnnualToggle';
import CustomPlanOfferHeading from './CustomPlanOfferHeading';
import CustomPlanStatusBadge from './CustomPlanStatusBadge';
import CustomPlanOfferCardActions from './CustomPlanOfferCardActions';

const HIDDEN_STATUSES = ['subscribed', 'amendment_requested'];

const CustomPlanOfferCard = ({ customPlanRequest, onSuccess }) => {
  const stripe = useStripe();
  const {
    acceptCustomPlan,
    confirmCustomPlanSubscribe,
    refresh,
    subscription,
  } = useBilling();
  const [acceptLoading, setAcceptLoading] = useState(false);
  const [selectedInterval, setSelectedInterval] = useState('annually');
  const toast = useToast();
  const {
    isOpen: isDeclineModalOpen,
    onOpen: onDeclineModalOpen,
    onClose: onDeclineModalClose,
  } = useDisclosure();
  const {
    isOpen: isAcceptModalOpen,
    onOpen: onAcceptModalOpen,
    onClose: onAcceptModalClose,
  } = useDisclosure();

  const {
    isOpen: isRequestChangesModalOpen,
    onOpen: onRequestChangesModalOpen,
    onClose: onRequestChangesModalClose,
  } = useDisclosure();
  const {
    isOpen: isRequestCustomPlanModalOpen,
    onOpen: onRequestCustomPlanModalOpen,
    onClose: onRequestCustomPlanModalClose,
  } = useDisclosure();
  const {
    isOpen: isCardlessConversionOpen,
    onOpen: onCardlessConversionOpen,
    onClose: onCardlessConversionClose,
  } = useDisclosure();

  const meCtx = useContext(MeContext);
  const { organisationStatus, cardlessTrialStartedAt } = meCtx.state;
  const isCardlessTrial =
    (organisationStatus === 'trial' ||
      organisationStatus === 'trial_expired') &&
    !!cardlessTrialStartedAt;

  if (
    !customPlanRequest ||
    !customPlanRequest.hasOfferedPlan ||
    HIDDEN_STATUSES.includes(customPlanRequest.status)
  ) {
    return null;
  }

  const offeredPlan = customPlanRequest.offeredPlan;

  const annualOption =
    customPlanRequest.annualOption ?? customPlanRequest.annual_option;
  const hasAnnualOption = !!annualOption;
  const currentlyAnnual = subscription?.plan?.interval === 'year';
  const isAnnualSelected =
    hasAnnualOption && (currentlyAnnual || selectedInterval === 'annually');
  const effectiveInterval = currentlyAnnual ? 'annually' : selectedInterval;

  const formatPrice = (amount) =>
    formatGbpFromMinorUnits(amount, { wholePounds: true });

  const handleAccept = async () => {
    if (!stripe) {
      toast({
        render: (props) => (
          <CustomToast
            status="error"
            title="Error"
            description="Payment system not ready. Please try again."
            onClose={props.onClose}
            id={props.id}
          />
        ),
      });
      return;
    }

    setAcceptLoading(true);

    try {
      const response = await acceptCustomPlan(
        customPlanRequest.id,
        hasAnnualOption ? effectiveInterval : undefined
      );

      if (response.requires_action) {
        const { error } = await stripe.confirmCardPayment(
          response.client_secret
        );

        if (error) {
          await confirmCustomPlanSubscribe(
            customPlanRequest.id,
            response.stripe_subscription_id,
            false,
            error.message
          );
          throw new Error(error.message);
        }

        await confirmCustomPlanSubscribe(
          customPlanRequest.id,
          response.stripe_subscription_id,
          true
        );
      }

      toast({
        render: (props) => (
          <CustomToast
            status="success"
            title={
              customPlanRequest.contractRequired &&
              customPlanRequest.status === 'offer_sent'
                ? 'Offer accepted'
                : 'Custom plan activated'
            }
            description={
              customPlanRequest.contractRequired &&
              customPlanRequest.status === 'offer_sent'
                ? 'Your contract is on the way.'
                : 'Your custom plan is now active.'
            }
            onClose={props.onClose}
            id={props.id}
          />
        ),
      });

      await refresh();
      onSuccess?.();
    } catch (error) {
      const errorMessage =
        error.response?.data?.errors?.[0] ||
        error.message ||
        'Something went wrong';

      toast({
        render: (props) => (
          <CustomToast
            status="error"
            title="Error"
            description={errorMessage}
            onClose={props.onClose}
            id={props.id}
          />
        ),
      });
    } finally {
      setAcceptLoading(false);
    }
  };

  const handleDeclineSuccess = async () => {
    onDeclineModalClose();
    await refresh();
  };

  const handleOpenAmendForm = () => {
    onRequestChangesModalClose();
    onRequestCustomPlanModalOpen();
  };

  const handleAmendFormSuccess = async () => {
    onRequestCustomPlanModalClose();
    await refresh();
  };

  const monthlyMinorEquivalent = monthlyEquivalentFromCustomOffer(
    offeredPlan?.amount,
    customPlanRequest.billingInterval
  );

  const savingPercent = hasAnnualOption
    ? annualOptionSavingPercent(annualOption)
    : null;

  const annualEquivalentIsExact =
    hasAnnualOption &&
    Number(annualOption.annualMonthlyEquivalentPence) * 12 ===
      Number(annualOption.annualAmountPence);
  const contractSuffix = customPlanRequest.contractLength
    ? `, ${customPlanRequest.contractLength} month contract`
    : '';

  return (
    <GradientBorderCard bg="white">
      <Flex justifyContent="space-between" alignItems="flex-start">
        <Text textStyle="bodySmall">Custom plan</Text>
        <CustomPlanStatusBadge status={customPlanRequest.status} />
      </Flex>
      <CustomPlanOfferHeading status={customPlanRequest.status} />
      {hasAnnualOption && savingPercent != null && (
        <Text textStyle="bodyFamilyRegular" fontSize="14px" mt={1}>
          <Text as="span" textStyle="bodyFamilyMedium" color="savingsGreen">
            Save {savingPercent}%
          </Text>{' '}
          with annual plan
        </Text>
      )}
      <Divider
        mt={4}
        border="0.95px solid"
        borderColor="orangePrimary"
        opacity={0.6}
      />
      <Box mt={8} mb={8}>
        <RequestedEntitlementsList
          requestedEntitlements={customPlanRequest.requestedEntitlements}
          title="Plan features:"
        />
      </Box>
      <Divider
        border="0.95px solid"
        borderColor="orangePrimary"
        opacity={0.6}
      />
      <Box mt={6}>
        <Text textStyle="heading5" mb={2}>
          {hasAnnualOption ? "Choose how you'd like to pay" : 'Your pricing'}
        </Text>
        {hasAnnualOption && (
          <Box mb={4}>
            <MonthlyAnnualToggle
              value={selectedInterval}
              onChange={setSelectedInterval}
              monthlyDisabled={currentlyAnnual}
            />
          </Box>
        )}
        {hasAnnualOption ? (
          isAnnualSelected ? (
            <>
              <Text textStyle="heading4">
                <Text
                  as="span"
                  textDecoration="line-through"
                  color="subheadingGrey"
                >
                  {formatPrice(annualOption.monthlyAmountPence)}
                </Text>{' '}
                {annualEquivalentIsExact ? '' : '≈ '}
                {formatGbpFromMinorUnits(
                  annualOption.annualMonthlyEquivalentPence
                )}{' '}
                <Text as="span" textStyle="bodyFamilyRegular" fontSize="16px">
                  /month
                </Text>{' '}
                <Text
                  as="span"
                  textStyle="headingFamilyMedium"
                  fontSize="14px"
                  color="savingsGreen"
                >
                  Save {formatPrice(annualOption.annualSavingPence)} a year
                </Text>
              </Text>
              <Text textStyle="bodyFamilyRegular" fontSize="14px" mt={1}>
                {formatPrice(annualOption.annualAmountPence)} billed annually
                {contractSuffix}
              </Text>
            </>
          ) : (
            <>
              <Text textStyle="heading4">
                {formatPrice(annualOption.monthlyAmountPence)}{' '}
                <Text as="span" textStyle="bodyFamilyRegular" fontSize="16px">
                  /month
                </Text>
              </Text>
              {customPlanRequest.contractLength && (
                <Text textStyle="bodyFamilyRegular" fontSize="16px" mt={1}>
                  for {customPlanRequest.contractLength} months
                </Text>
              )}
            </>
          )
        ) : (
          <Text textStyle="heading4">
            {formatPrice(monthlyMinorEquivalent ?? offeredPlan?.amount ?? 0)}{' '}
            <Text as="span" textStyle="bodyFamilyRegular" fontSize="16px">
              / per month, billed{' '}
              {customOfferBilledIntervalPhrase(
                customPlanRequest.billingInterval
              )}
            </Text>{' '}
            {customPlanRequest.contractLength && (
              <Text as="span" textStyle="bodyFamilyRegular" fontSize="16px">
                ({customPlanRequest.contractLength} months contract)
              </Text>
            )}
          </Text>
        )}
      </Box>
      <CustomPlanOfferCardActions
        status={customPlanRequest.status}
        contractRequired={customPlanRequest.contractRequired}
        onRequestChanges={onRequestChangesModalOpen}
        onAccept={
          isCardlessTrial &&
          (!customPlanRequest.contractRequired ||
            customPlanRequest.status === 'signature_received')
            ? onCardlessConversionOpen
            : onAcceptModalOpen
        }
        acceptLoading={acceptLoading}
      />
      <DeclineCustomPlanModal
        isOpen={isDeclineModalOpen}
        onClose={onDeclineModalClose}
        customPlanRequestId={customPlanRequest.id}
        onSuccess={handleDeclineSuccess}
      />
      <AcceptCustomPlanModal
        isOpen={isAcceptModalOpen}
        onClose={onAcceptModalClose}
        customPlanRequestId={customPlanRequest.id}
        onSuccess={handleAccept}
        currentPlan={customPlanRequest}
        newPlan={customPlanRequest}
        formatPrice={formatPrice}
        billingIntervalOverride={
          hasAnnualOption ? effectiveInterval : undefined
        }
        amountOverride={
          hasAnnualOption
            ? isAnnualSelected
              ? annualOption.annualAmountPence
              : annualOption.monthlyAmountPence
            : undefined
        }
        loading={acceptLoading}
      />
      <RequestChangesModal
        isOpen={isRequestChangesModalOpen}
        onClose={onRequestChangesModalClose}
        onOpenDeclineModal={onDeclineModalOpen}
        onOpenForm={handleOpenAmendForm}
      />
      <RequestCustomPlanModal
        isOpen={isRequestCustomPlanModalOpen}
        onClose={onRequestCustomPlanModalClose}
        onSuccess={handleAmendFormSuccess}
        initialValues={customPlanRequest?.requestedEntitlements}
        amendPlanId={customPlanRequest?.id}
        selectedInterval={subscription?.plan?.interval ?? 'year'}
      />
      {isCardlessTrial && (
        <CardlessTrialConversionModal
          isOpen={isCardlessConversionOpen}
          onClose={onCardlessConversionClose}
          customPlanOffer={customPlanRequest}
          customPlanInterval={hasAnnualOption ? effectiveInterval : undefined}
        />
      )}
    </GradientBorderCard>
  );
};

export default CustomPlanOfferCard;
